import { Paperclip } from "lucide-react";

import type { AttachmentNote, ContactNote, DealNote } from "../types";
import { useSignedAttachment } from "../providers/commons/useSignedAttachment";

/**
 * One attachment, resolved to a currently-valid signed URL. Split into its own
 * component because the hook must run per attachment, and hooks cannot be
 * called inside a `.map` in the parent.
 */
const AttachmentImage = ({ attachment }: { attachment: AttachmentNote }) => {
  const href = useSignedAttachment(attachment.path, attachment.src);
  return (
    <a
      href={href}
      title={attachment.title}
      target="_blank"
      rel="noopener noreferrer"
      className="block"
      onClick={(e) => e.stopPropagation()}
    >
      <img
        src={href}
        alt={attachment.title}
        className="w-[200px] h-[100px] object-cover cursor-pointer object-left border border-border"
      />
    </a>
  );
};

const AttachmentLink = ({ attachment }: { attachment: AttachmentNote }) => {
  const href = useSignedAttachment(attachment.path, attachment.src);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline hover:no-underline"
      onClick={(e) => e.stopPropagation()}
    >
      {attachment.title}
    </a>
  );
};

/**
 * Displays persisted note attachments in note show/list views.
 *
 * This component receives a full note record and renders all attachments.
 *
 * @param props.note - Note record containing attachments to render.
 * @returns `null` when there are no attachments, otherwise attachment previews and links.
 */
export const NoteAttachments = ({ note }: { note: ContactNote | DealNote }) => {
  if (!note.attachments || note.attachments.length === 0) {
    return null;
  }

  const imageAttachments = note.attachments.filter(
    (attachment: AttachmentNote) => isImageMimeType(attachment.type),
  );
  const otherAttachments = note.attachments.filter(
    (attachment: AttachmentNote) => !isImageMimeType(attachment.type),
  );

  return (
    <div className="mt-2 flex flex-col gap-2">
      {imageAttachments.length > 0 && (
        <div className="grid grid-cols-4 gap-8">
          {imageAttachments.map((attachment: AttachmentNote, index: number) => (
            <div key={index}>
              <AttachmentImage attachment={attachment} />
            </div>
          ))}
        </div>
      )}
      {otherAttachments.length > 0 &&
        otherAttachments.map((attachment: AttachmentNote, index: number) => (
          <div key={index} className="flex items-center gap-2">
            <Paperclip className="w-4 h-4" />
            <AttachmentLink attachment={attachment} />
          </div>
        ))}
    </div>
  );
};

/**
 * Checks whether a mime type corresponds to an image.
 *
 * @param mimeType - The attachment mime type.
 * @returns `true` when the mime type starts with `image/`.
 */
const isImageMimeType = (mimeType?: string): boolean => {
  if (!mimeType) {
    return false;
  }
  return mimeType.startsWith("image/");
};
